import { IAdminUserAdminPost, IAdminUserGuardKindPost, IAdminUserTiltDelete, IAdminUserTiltPost } from "@api/admin.int";
import { IUserPub, TAdminLevel, TGuardKind } from "@api/auth.int";
import { IGuardSendMailPostApi, IProfileGetApi } from "@api/profile.int";
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, FormControl, FormLabel, Link, ListItem, MenuItem, Text, Textarea, Tooltip, UnorderedList, useBoolean } from "@chakra-ui/react";
import { AdminMenu, AdminSubMenu } from "components/AdminMenu";
import { useDeepAuth, useVVApi } from "components/api";
import { ADMIN_LEVELS, ADMIN_LEVELS_ARR, GUARD_KINDS, GUARD_KINDS_ARR } from "components/appComponents";
import { DeepErrorBox, DeepErrorContainer } from "components/misc";
import { useVVStore } from "components/store";
import { AFFAIR_DESCRIPTION_MAX, AFFAIR_DESCRIPTION_MIN } from "components/utils";
import { VizvButtonBack, VizvButtonPrimary, VizvCard, VizvCardAction, VizvCardActionStrip, VizvCardContent, VizvCardHeaderPopup, VizvMainCol } from "components/vizvComponents";
import { useVVNav } from "components/vvNav";
import { GuardKindBox, GuardKindContainer } from "features/profile/Profile";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { FaBolt } from "react-icons/fa";
import { MdCheckBox, MdCheckBoxOutlineBlank, MdEdit, MdLock, MdLockOpen, MdStar } from "react-icons/md";
import { NavLink, Outlet, useNavigate, useParams } from "react-router";



export interface IGuardContext {
	guard: IUserPub;
	setGuard: (guard: IUserPub) => void;
}
export const GuardContext = createContext<IGuardContext>({
	guard: undefined as any,
	setGuard: (guard: IUserPub) => { },
});

export function GuardLayout({ isDetail = false }: { isDetail?: boolean }) {
	const vvNav = useVVNav();
	const routeParams = useParams();

	const isMobile = useVVStore(state => state.isMobile());
	const api = useVVApi();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [profile, setProfile] = useState<IUserPub>();

	useEffect(() => {
		(async () => {
			if (!routeParams.userKey)
				return;
			try {
				setIsLoading(true);
				setError('');
				const resp = await api.fetch<IProfileGetApi>({
					url: '/auth/profile/:userKey', method: 'GET',
					params: { userKey: routeParams.userKey }
				});
				setProfile(resp.user);
			} catch (ex) {
				setError('' + ex);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [routeParams.userKey]);

	const ctx = useMemo(() => ({
		guard: profile!,
		setGuard: setProfile
	}), [profile, setProfile]);



	return <GuardContext.Provider value={ctx} >
		<VizvMainCol isDetail={isDetail}>
			<VizvCard onClose={vvNav.toRoot}
				minH='40vh' {...!isMobile && { minW: '30em' }}
			>
				<DeepErrorBox error={error} isLoading={isLoading} />
				{profile && <>
					<VizvCardHeaderPopup variant="blue" >
						{profile.guard?.name || profile.userName}
					</VizvCardHeaderPopup>
					<Outlet />
				</>}
			</VizvCard>
		</VizvMainCol>
	</GuardContext.Provider>
}

export function GuardDashboard() {
	const isMobile = useVVStore(state => state.isMobile());
	const auth = useDeepAuth();
	const ctx = useContext(GuardContext);
	const isMe = ctx.guard.userKey === auth.signedUser?.userKey;
	const profile = ctx.guard!;
	const api = useVVApi();
	const [isLoading, setIsLoading] = useBoolean();
	const [error, setError] = useState('');


	const adminTilt = async () => {
		const message = window.prompt("Írj rövid magyarázatot:", "");
		if (!message)
			return;
		try {
			setIsLoading.on();
			setError('');

			const resp = await api.fetch<IAdminUserTiltPost>({
				url: '/admin/user/:userKey/tilt',
				method: 'POST',
				payload: {
					message,
				},
				params: {
					userKey: profile.userKey
				}
			});
			ctx.setGuard(resp.user);
		} catch (ex) {
			setError('' + ex);
		} finally {
			setIsLoading.off();
		}
	}

	const adminTiltOff = async () => {
		const message = window.prompt("Írj rövid magyarázatot:", "");
		if (!message)
			return;
		try {
			setIsLoading.on();
			setError('');
			const resp = await api.fetch<IAdminUserTiltDelete>({
				url: '/admin/user/:userKey/tilt',
				method: 'DELETE',
				payload: {
					message,
				},
				params: {
					userKey: profile.userKey
				}
			});
			ctx.setGuard(resp.user);
		} catch (ex) {
			setError('' + ex);
		} finally {
			setIsLoading.off();
		}

	}

	const adminSetAdmin = async (adminLevel: 0 | 1 | 2) => {
		try {
			setIsLoading.on();
			setError('');

			const resp = await api.fetch<IAdminUserAdminPost>({
				url: '/admin/user/:userKey/admin/:adminLevel',
				method: 'POST',
				params: {
					userKey: profile.userKey,
					adminLevel: adminLevel + '',
				}
			});
			ctx.setGuard(resp.user);
		} catch (ex) {
			setError('' + ex);
		} finally {
			setIsLoading.off();
		}
	}

	const adminSetGuardKind = async (guardKind: TGuardKind) => {
		try {
			setIsLoading.on();
			setError('');

			const resp = await api.fetch<IAdminUserGuardKindPost>({
				url: '/admin/user/:userKey/guard/kind/:guardKind',
				method: 'POST',
				params: {
					userKey: profile.userKey,
					guardKind,
				}
			});
			ctx.setGuard(resp.user);
		} catch (ex) {
			setError('' + ex);
		} finally {
			setIsLoading.off();
		}

	}



	console.log('GUARD', ctx.guard);

	return <DeepErrorContainer isLoading={isLoading} error={error} onErrorClose={() => setError('')}>
		<VizvCardContent display='flex' flexDir='row' gap={3} {...isMobile && { flexDir: 'column' }} >
			<UnorderedList className=''>
				{profile.isAdmin > 0 && <ListItem>{ADMIN_LEVELS[profile.isAdmin].name}</ListItem>}
				{profile.guard
					? <>
						<ListItem>{profile.guard.locationTitle}</ListItem>
						<ListItem>{profile.guard.message}</ListItem>
					</>
					: <ListItem>A felhasználónak nincs VízVédő profilja</ListItem>
				}
				{profile.userSummary.jelentettCount > 0 && <ListItem><Link className="xx2" as={NavLink} to={`/profile-affairs/${profile.userKey}`}>{profile.userSummary.jelentettCount} bejelentés</Link></ListItem>}
				{profile.userSummary.imageCount > 0 && <ListItem>{profile.userSummary.imageCount} kép</ListItem>}
				{profile.userSummary.videoCount > 0 && <ListItem>{profile.userSummary.videoCount} videó</ListItem>}
				{(isMe || auth.signedUser?.isAdmin) && ctx.guard.tilted && <Alert status="error" variant='solid' flexDirection='column' alignItems='flex-start' maxWidth='20em'>
					<Box display='flex'>
						<AlertIcon />
						<AlertTitle>Tiltott felhasználó</AlertTitle>
					</Box>
					<AlertDescription>
						A felhasználó nem tud új bejelentést tenni és nem látszik a keresésben.<br />"{ctx.guard.tilted.message}"
					</AlertDescription>
				</Alert>}

				{!isMe && auth.signedUser?.isAdmin && <ListItem>
					<AdminMenu>
						{!ctx.guard.tilted
							? <Tooltip label="A felhasználó nem tud új bejelentést tenni és nem látszik a keresésben" placement="right" hasArrow={true} bg='gray' color='black'>
								<MenuItem onClick={adminTilt} icon={<MdLock />} >Bejelentések tiltása</MenuItem>
							</Tooltip>
							: <MenuItem onClick={adminTiltOff} icon={<MdLockOpen />} >Tiltás feloldása</MenuItem>}
						<AdminSubMenu label="Admin jog" icon={<FaBolt />}>
							{ADMIN_LEVELS_ARR.map(level =>
								<MenuItem onClick={() => adminSetAdmin(level.key)} icon={ctx.guard.isAdmin == level.key ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />} isDisabled={ctx.guard.isAdmin == level.key} >{level.name}</MenuItem>
							)}
						</AdminSubMenu>
						{ctx.guard.guard && <>
							<AdminSubMenu label="Rang:" icon={<MdStar />}>
								{GUARD_KINDS_ARR.map(kind =>
									<MenuItem key={kind.key} onClick={() => adminSetGuardKind(kind.key)} icon={ctx.guard.guard?.kind == kind.key ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />} isDisabled={ctx.guard.guard?.kind == kind.key} >{kind.name}</MenuItem>
								)}


							</AdminSubMenu>
						</>}


					</AdminMenu>
				</ListItem>}

			</UnorderedList>

			<Box display='flex' flexDir='column' flex='0 0 50%' alignItems='center'>
				<GuardKindContainer display='inline-flex' flexDir='row' width='200px' justifyContent='space-evenly' py={8} mb={1} >
					<GuardKindBox guardKind={profile.guard?.kind || '00'} guardName={profile.guard?.name || profile.userName} />
				</GuardKindContainer>
				{GUARD_KINDS[profile.guard?.kind || '00'].name}
			</Box>



		</VizvCardContent>
		<VizvCardActionStrip mt='auto' pr={0}>
			{auth.userConfirmed ?
				<VizvCardAction
					//to={`/guard/${profile.userKey}/contact`}
					to={`contact`}
				//onClick={e => {e.preventDefault(); alert('Hamarosan...')}}
				>Felveszem a kapcsolatot</VizvCardAction>
				: <VizvCardAction onClick={() => auth.showSignIn('A kapcsolatfelvételhez be kell jelentkezned!')}>Felveszem a kapcsolatot</VizvCardAction>
			}
		</VizvCardActionStrip>
	</DeepErrorContainer>
}


export function GuardContact() {
	const [isLoading, setIsLoading] = useBoolean();
	const [error, setError] = useState('');
	const ctx = useContext(GuardContext);
	const profile = ctx.guard!;
	const nav = useNavigate();
	const [message, setMessage] = useState('');
	const api = useVVApi();

	const onSubmit = async (e: React.SyntheticEvent) => {
		e.preventDefault();
		setIsLoading.on();
		setError('');
		try {
			const resp = await api.fetch<IGuardSendMailPostApi>({
				url: '/auth/profile/:userKey/send-mail',
				method: 'POST',
				payload: {
					message,
				},
				params: {
					userKey: profile.userKey
				}
			});
			alert('Az üzenetet elküldtük');
			nav('..');

		} catch (ex) {
			setError('' + ex);
		} finally {
			setIsLoading.off();
		}
	}


	return <Box p={2} >
		Felveheted "{profile.guard?.name || profile.userName}" vízőrrel a kapcsolatot!
		<FormControl>
			<FormLabel>Üzenet</FormLabel>
			<Textarea
				autoFocus
				value={message} onChange={e => setMessage(e.target.value)}
				rows={7} cols={60} />
		</FormControl>

		<VizvCardActionStrip mt='auto'>
			<VizvButtonBack onClick={() => nav('..')} ml={2} >Mégsem</VizvButtonBack>
			<VizvButtonPrimary isDisabled={!message || message.length < AFFAIR_DESCRIPTION_MIN || message.length > AFFAIR_DESCRIPTION_MAX} mr={2}
				isLoading={isLoading} type='submit'
				onClick={onSubmit} >Elküldöm</VizvButtonPrimary>

		</VizvCardActionStrip>
	</Box>

}
